import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, catchError, filter, Observable, switchMap, take, throwError } from 'rxjs';
import { SessionManager } from '../services/auth/session-manager/session-manager.service';
import { AccessResponse } from '../models/auth/session-data.model';
import { RedirectReasonCode } from '../models/others/redirect-reason.model';
import { NO_AUTHORIZATION_HEADER } from './http.context';

@Injectable({
    providedIn: 'root',
})
export class AuthInterceptor implements HttpInterceptor {

    private isRefreshing = false;
    private refreshTokenSubject: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(null);

    constructor(
        private readonly sessionManager: SessionManager
    ) { }

    intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {

        let request = req;

        const skipAuthHeader = req.context.get(NO_AUTHORIZATION_HEADER);
        if (skipAuthHeader) {
            return next.handle(request);
        }


        const token = sessionStorage.getItem('token');


        if (!!token) {
            request = this.addTokenHeader(req, token);
        }

        return next.handle(request).pipe(
            catchError((error) => {
                if (error instanceof HttpErrorResponse && error.status === 401) {
                    return this.handleTokenRefresh(request, next);
                }
                return throwError(() => error);
            })
        );
    }

    private addTokenHeader(request: HttpRequest<any>, token: string): HttpRequest<any> {
        const session = sessionStorage.getItem('session');

        if (!session) {
            return request;
        }

        const formatSession = JSON.parse(session);
        return request.clone({
            setHeaders: {
                'Authorization': `Bearer ${token}`,
                'run': formatSession.data.run.split('-')[0]
            },
        });
    }

    private handleTokenRefresh(request: HttpRequest<any>, next: HttpHandler) {
        if (!this.isRefreshing) {
            this.isRefreshing = true;
            this.refreshTokenSubject.next(null);

            return this.sessionManager.claveUnicaRefresh().pipe(
                catchError((refreshErr) => {
                    this.isRefreshing = false;
                    this.sessionManager.claveUnicaLogout(RedirectReasonCode.REFRESH_TIMEOUT);
                    return throwError(() => refreshErr);
                }),
                switchMap((response: AccessResponse) => {
                    this.isRefreshing = false;
                    this.sessionManager.setSessionStateFromToken(response);
                    this.refreshTokenSubject.next(response.accessToken);

                    return next.handle(this.addTokenHeader(request, response.accessToken)).pipe(
                        catchError((error) => {
                            if (error instanceof HttpErrorResponse && error.status === 401) {
                                this.sessionManager.claveUnicaLogout(RedirectReasonCode.REFRESH_ERROR);
                            }
                            return throwError(() => error);
                        })
                    );
                })
            );
        } else {
            return this.refreshTokenSubject.pipe(
                filter(token => token !== null),
                take(1),
                switchMap((token) => next.handle(this.addTokenHeader(request, token!)))
            );
        }
    }

}